<template>
    <div>
        <div class="click-item mdl-js-button mdl-js-ripple-effect" @click="openThread">
            <div class="draft-text">
                <div class="draft-title mdl-color-text--grey-900">
                    {{ title }}
                </div>
                <div v-for="(text, i) in draftData.texts" :key="'text-' + i" class="mdl-color-text--grey-600">
                    {{ text }}
                </div>
                <div v-if="mediaLabel" class="mdl-color-text--grey-600">
                    <i>{{ mediaLabel }}</i>
                </div>
            </div>
            <button :id="menuId" class="more-button mdl-button mdl-js-button mdl-button--icon" @click.stop="menu.toggle()">
                <i class="material-icons">more_vert</i>
            </button>
        </div>
        <ul id="draft-menu"
            class="mdl-menu mdl-js-menu mdl-js-ripple-effect mdl-menu--bottom-right" :data-mdl-for="menuId"
        >
            <li v-if="draftData.texts.length > 0" class="mdl-menu__item" @click="copyText">
                {{ $t('drafts.copy') }}
            </li>
            <li class="mdl-menu__item" @click="deleteDrafts">
                {{ $t('drafts.delete') }}
            </li>
        </ul>
    </div>
</template>

<script>

import { Api, Util } from '@/utils';
import { componentHandler } from '@/lib/material.js';

export default {
    name: 'DraftItem',
    // Read from the prop, not data(): Drafts.vue fills in the title after an async lookup.
    props: [ 'draftData' ],

    data () {
        return {
            menu: null,
        };
    },

    computed: {
        menuId () {
            return 'draft-menu-' + this.draftData.conversation_id;
        },

        title () {
            return this.draftData.title || this.$t('drafts.unknown');
        },

        // Media drafts point at files on the phone, which the web can't load, so only say what they are.
        mediaLabel () {
            const labels = [];
            for (const mimeType of this.draftData.media) {
                const label = this.labelFor(mimeType);
                if (labels.indexOf(label) === -1) {
                    labels.push(label);
                }
            }

            return labels.join(', ');
        }
    },

    mounted () {
        let menu_el = this.$el.querySelector("#draft-menu");
        componentHandler.upgradeElement(menu_el);

        this.menu = menu_el.MaterialMenu;
    },

    methods: {
        labelFor (mimeType) {
            if (mimeType.indexOf('image/') === 0) {
                return this.$t('drafts.image');
            } else if (mimeType.indexOf('video/') === 0) {
                return this.$t('drafts.video');
            } else if (mimeType.indexOf('audio/') === 0) {
                return this.$t('drafts.audio');
            } else if (mimeType.indexOf('vcard') !== -1) {
                return this.$t('drafts.contact');
            }

            return this.$t('drafts.attachment');
        },

        // The thread loads text drafts into the send bar itself (Thread.vue loadDrafts).
        openThread () {
            this.$router.push({
                name: this.draftData.archive ? 'thread-archived' : 'thread',
                params: { threadId: this.draftData.conversation_id, isRead: this.draftData.read }
            });
        },

        copyText () {
            const text = this.draftData.texts.join('\n');
            navigator.clipboard.writeText(text)
                .then(() => Util.snackbar(this.$t('drafts.copied')))
                .catch(() => { });
        },

        deleteDrafts () {
            const options = {
                okText: this.$t('dialog.continue'),
                cancelText: this.$t('dialog.cancel'),
                animation: 'fade'
            };

            const conversationId = this.draftData.conversation_id;
            this.$dialog.confirm(this.$t('drafts.deleteconfirm'), options)
                .then(() => {
                    Api.drafts.delete(conversationId);
                    Util.snackbar(this.$t('drafts.deleted'));

                    // Remove locally instead of refetching: the delete request isn't awaited, so
                    // a refetch could still return these drafts.
                    this.$emit('deleted', conversationId);
                })
                .catch(() => { }); // Cancelled
        }
    },

};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    @import "../../../assets/scss/_vars.scss";

    .item, .click-item {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        padding: 12px;
        line-height: 18px;
        box-sizing: border-box;
    }

    .draft-text {
        flex: 1;
        min-width: 0;
        overflow-wrap: break-word;
    }

    .draft-title {
        font-weight: 500;
    }

    .more-button {
        flex-shrink: 0;
        margin-left: 8px;
    }

    .item:hover, .click-item:hover {
        background: #E0E0E0;
    }

    .click-item:hover {
        cursor: pointer;
    }

    body.dark {
        .item:hover, .click-item:hover {
            background: #202020;
        }
    }

</style>
